import { type ReactNode, useId } from 'react';
import { cn } from '@/lib/cn';

interface FieldProps {
  label: string;
  /** Inline help text shown below the input. */
  hint?: string;
  /** Validation error — overrides hint when present. */
  error?: string;
  required?: boolean;
  /** A render-prop that receives the id to wire onto the input. */
  children: (props: { id: string; 'aria-describedby'?: string; 'aria-invalid'?: true }) => ReactNode;
}

/**
 * Label + input + error wrapper. Use with react-hook-form:
 *
 *   <Field label="Username" error={errors.username?.message} required>
 *     {({ id, ...rest }) => <Input id={id} {...register('username')} {...rest} />}
 *   </Field>
 */
export function Field({ label, hint, error, required, children }: FieldProps) {
  const id = useId();
  const descId = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-brand-charcoal">
        {label}
        {required && <span className="ml-0.5 text-brand-danger">*</span>}
      </label>
      {children({
        id,
        ...(descId ? { 'aria-describedby': descId } : {}),
        ...(error ? { 'aria-invalid': true } : {}),
      })}
      {error ? (
        <p id={descId} className="text-xs text-brand-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={descId} className={cn('text-xs text-brand-gray-mid')}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
